// src/utils/performance.ts

import { type ImageProps } from "~/types/shared"

type ImageBreakpoint = {
  breakpoint: number
  sizeFactor: number
}

interface ImageSizeConfig {
  // Default breakpoints aligned with Tailwind's defaults
  breakpoints: ImageBreakpoint[]
  // Maximum allowed image width relative to viewport
  maxViewportWidth: number
  // Absolute maximum width (in pixels)
  maxWidth: number
  // Whether image should be full width on mobile
  fullWidthMobile?: boolean
}

const defaultConfig: ImageSizeConfig = {
  breakpoints: [
    { breakpoint: 640, sizeFactor: 1 },    // sm
    { breakpoint: 768, sizeFactor: 0.9 },  // md
    { breakpoint: 1024, sizeFactor: 0.8 }, // lg
    { breakpoint: 1280, sizeFactor: 0.7 }, // xl
    { breakpoint: 1536, sizeFactor: 0.6 }, // 2xl
  ],
  maxViewportWidth: 0.95, // 95% of viewport max
  maxWidth: 1920,
  fullWidthMobile: true
}

export function getImageSizes(
  props: Pick<ImageProps, "width" | "height">, 
  config: Partial<ImageSizeConfig> = {}
): string {
  const finalConfig = { ...defaultConfig, ...config }
  const { breakpoints, maxViewportWidth, maxWidth, fullWidthMobile } = finalConfig

  // Sort breakpoints by size (ascending)
  const sortedBreakpoints = [...breakpoints].sort((a, b) => a.breakpoint - b.breakpoint)

  // Calculate the natural aspect ratio
//   const aspectRatio = props.width / props.height

  // Build the sizes string
  const sizes = sortedBreakpoints.map((bp, index) => {
    const isFirstBreakpoint = index === 0
    const viewportWidth = Math.min(
      bp.sizeFactor * 100,
      maxViewportWidth * 100
    )

    // For mobile/first breakpoint
    if (isFirstBreakpoint && fullWidthMobile) {
      return `(max-width: ${bp.breakpoint}px) 100vw`
    }

    return `(max-width: ${bp.breakpoint}px) ${viewportWidth}vw`
  })

  // Add the final size for larger viewports
  const finalWidth = Math.min(props.width, maxWidth)
  sizes.push(`${finalWidth}px`)

  return sizes.join(', ')
}

// Example usage:
// For hero images that need to be prominent
const heroConfig: Partial<ImageSizeConfig> = {
  breakpoints: [
    { breakpoint: 640, sizeFactor: 1 },
    { breakpoint: 768, sizeFactor: 0.95 },
    { breakpoint: 1024, sizeFactor: 0.9 },
    { breakpoint: 1280, sizeFactor: 0.85 },
    { breakpoint: 1536, sizeFactor: 0.8 },
  ],
  maxViewportWidth: 0.95,
  maxWidth: 1920,
  fullWidthMobile: true
}

// For smaller feature images
const featureConfig: Partial<ImageSizeConfig> = {
  breakpoints: [
    { breakpoint: 640, sizeFactor: 0.9 },
    { breakpoint: 768, sizeFactor: 0.7 },
    { breakpoint: 1024, sizeFactor: 0.5 },
    { breakpoint: 1280, sizeFactor: 0.4 },
    { breakpoint: 1536, sizeFactor: 0.3 },
  ],
  maxViewportWidth: 0.9,
  maxWidth: 800,
  fullWidthMobile: false
}

// Helper function to get common image configurations
export function getImageConfig(type: 'hero' | 'feature' | 'thumbnail' | 'banner'): Partial<ImageSizeConfig> {
  switch (type) {
    case 'hero':
      return heroConfig
    case 'feature':
      return featureConfig
    case 'thumbnail':
      return {
        breakpoints: [
          { breakpoint: 640, sizeFactor: 0.4 },
          { breakpoint: 768, sizeFactor: 0.3 },
          { breakpoint: 1024, sizeFactor: 0.25 },
          { breakpoint: 1280, sizeFactor: 0.2 },
        ],
        maxViewportWidth: 0.8,
        maxWidth: 400,
        fullWidthMobile: false
      }
    case 'banner':
      return {
        breakpoints: [
          { breakpoint: 640, sizeFactor: 1 },
          { breakpoint: 768, sizeFactor: 1 },
          { breakpoint: 1024, sizeFactor: 0.95 },
          { breakpoint: 1280, sizeFactor: 0.9 },
        ],
        maxViewportWidth: 1,
        maxWidth: 2560,
        fullWidthMobile: true
      }
    default:
      return defaultConfig
  }
}

// Usage in HeroImage component:
// const sizes = getImageSizes(
//   { width: 1350, height: 900 }, 
//   getImageConfig('hero')
// )

export function getDynamicBlurDataUrl(width: number, height: number): string {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="b" color-interpolation-filters="sRGB">
          <feGaussianBlur stdDeviation="12"/>
          <feColorMatrix type="matrix" values="0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0 0 0 1 0"/>
        </filter>
      </defs>
      <rect width="100%" height="100%" filter="url(#b)"/>
    </svg>
  `

  const toBase64 = (str: string): string =>
    typeof window === 'undefined'
      ? Buffer.from(str).toString('base64')
      : new TextEncoder().encode(str).reduce((acc, byte) => 
          acc + String.fromCharCode(byte), ''
        )

  return `data:image/svg+xml;base64,${toBase64(svg.trim())}`
}

export function getImageLoadingStrategy(src: string, viewport: "above" | "below" = "below"): {
  loading: "eager" | "lazy"
  fetchPriority: "high" | "auto" | "low"
  decoding: "async" | "sync"
} {
  const isHighPriority = 
    viewport === "above" || 
    src.includes("hero") || 
    src.includes("logo")

  return {
    loading: isHighPriority ? "eager" : "lazy",
    fetchPriority: isHighPriority ? "high" : "auto",
    decoding: "async"
  }
}

// Properly type the performance entries
interface LayoutShiftEntry extends PerformanceEntry {
  value: number
  hadRecentInput: boolean
}

interface LargestContentfulPaintEntry extends PerformanceEntry {
  element: Element
  size: number
  startTime: number
}

export function monitorPerformance(): void {
  if (typeof window === 'undefined') return
  // Dev-only web-vitals tracing. These are high-frequency debug values, not
  // actionable application faults; production web-vitals reporting goes through
  // GA elsewhere, so keep this as a local dev console signal, not a drain POST.
  if (process.env.NODE_ENV !== 'development') return

  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach(entry => {
      switch (entry.entryType) {
        case 'largest-contentful-paint': {
          const lcp = entry as LargestContentfulPaintEntry
          console.debug('LCP:', lcp.startTime)
          break
        }
        case 'layout-shift': {
          const cls = entry as LayoutShiftEntry
          console.debug('CLS:', cls.value)
          break
        }
        case 'longtask':
          console.debug('Long Task:', entry.duration)
          break
      }
    })
  })

  observer.observe({ 
    entryTypes: ['largest-contentful-paint', 'layout-shift', 'longtask'] 
  })
}

interface DeviceCapabilities {
  hasWebGL: boolean
  deviceMemory: number
  hardwareConcurrency: number
  prefersReducedMotion: boolean
  isHighPerformance: boolean
}

let cachedWebGLProbe: boolean | null = null
let interactiveGlobeDisabled = false

/** DOM event fired when COBE init fails after a successful WebGL probe. */
export const INTERACTIVE_GLOBE_UNAVAILABLE_EVENT = 'globe:interactive-unavailable'

/**
 * Records that the interactive globe cannot run for this page session.
 *
 * Scratch-canvas probing can succeed while COBE init still fails (context attr
 * mismatch, context slot limits, driver quirks). Clearing the cache alone would
 * re-probe to `true`; this pins {@link canAcquireWebGLContext} to `false` and
 * notifies {@link GlobalInsightsWrapper} to switch to the static globe.
 */
export function markInteractiveGlobeUnavailable(): void {
  interactiveGlobeDisabled = true
  cachedWebGLProbe = false
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(INTERACTIVE_GLOBE_UNAVAILABLE_EVENT))
  }
}

/**
 * Returns whether the interactive globe is still allowed for this page session.
 *
 * @returns {boolean} False after {@link markInteractiveGlobeUnavailable} runs.
 */
export function isInteractiveGlobeAvailable(): boolean {
  return !interactiveGlobeDisabled
}

/**
 * Subscribes to interactive-globe availability changes (for `useSyncExternalStore`).
 *
 * @param {() => void} onStoreChange - Callback when availability flips to unavailable.
 * @returns {() => void} Unsubscribe function.
 */
export function subscribeInteractiveGlobeAvailability(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined
  }
  window.addEventListener(INTERACTIVE_GLOBE_UNAVAILABLE_EVENT, onStoreChange)
  return () => window.removeEventListener(INTERACTIVE_GLOBE_UNAVAILABLE_EVENT, onStoreChange)
}

/**
 * Returns whether the browser can acquire a WebGL context on a scratch canvas.
 *
 * Some clients expose `WebGLRenderingContext` but return `null` from
 * `getContext()` (software renderers, locked-down environments, headless bots).
 * COBE/phenomenon then throws when calling `.enable` on a null GL handle — so
 * callers must probe a real context, not just the constructor's presence.
 *
 * The result is cached for the page lifetime and the probe context is released
 * via `WEBGL_lose_context` so tight per-tab WebGL limits aren't consumed before
 * the real globe mounts.
 *
 * @returns {boolean} True when webgl/webgl2/experimental-webgl succeeds.
 */
export function canAcquireWebGLContext(): boolean {
  if (typeof window === 'undefined') return false
  if (interactiveGlobeDisabled) return false
  if (cachedWebGLProbe !== null) return cachedWebGLProbe
  if (!window.WebGLRenderingContext) {
    cachedWebGLProbe = false
    return false
  }

  try {
    const canvas = document.createElement('canvas')
    const gl =
      canvas.getContext('webgl2', {
        failIfMajorPerformanceCaveat: true,
      }) ??
      canvas.getContext('webgl', {
        failIfMajorPerformanceCaveat: true,
      }) ??
      canvas.getContext('experimental-webgl')
    if (gl !== null && 'getExtension' in gl) {
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
    cachedWebGLProbe = gl !== null
    return cachedWebGLProbe
  } catch {
    cachedWebGLProbe = false
    return false
  }
}

export function getDeviceCapabilities(): DeviceCapabilities {
  if (typeof window === 'undefined') {
    return {
      hasWebGL: false,
      deviceMemory: 0,
      hardwareConcurrency: 0,
      prefersReducedMotion: false,
      isHighPerformance: false
    }
  }

  const nav = window.navigator as Navigator & { deviceMemory?: number }
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const hasWebGL = canAcquireWebGLContext()

  const capabilities = {
    hasWebGL,
    deviceMemory: nav.deviceMemory ?? 4,
    hardwareConcurrency: navigator.hardwareConcurrency ?? 4,
    prefersReducedMotion,
    isHighPerformance: false
  }

  capabilities.isHighPerformance = 
    hasWebGL &&
    !capabilities.prefersReducedMotion &&
    capabilities.deviceMemory >= 4 &&
    capabilities.hardwareConcurrency >= 4

  return capabilities
} 

// Base64 images for hero section

export const heroBase64Dark = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAJCAIAAABi7eEAAAAACXBIWXMAAAsTAAALEwEAmpwYAAABaklEQVQYlUWQS0sCYRhGn/f7vknHMVLDmdDCIFoG7e1ORJvqDwSF/6ALRNR/6r6UjGJaV3axUioJzbTMblAz87Zw0bM/nMNDpCLTM5Oexztb2yCG54IFSIBdkAQAAimNiJhZtujB/v4+27bjsfj66lIg0FqqVJl5bn52ZCRpdURzuZvuRFcqlTo+tlWsI9poNEoP9y9GNH1wVLgtEkEQEt09e3u79fobCSqVn8rlkpS6DEUs0zKvr65mpieGh5KWGdpPp53fn3yhcJm9qFUrYOF67nOt1nh9VWPjo/mbPEg7O8+dZk8cxwEcKK14lwMBBLAL11cuPoJAC8trQkrTslYWFxI9U349KKUyjM6v7yroJ6CH7++OKo82hAFAZTIZgB3PEy263/c1ONC7uXEI5JsnCOn/eH8BaSACAFAA8IMMpbdD+trNGP7XTBFQISMcJ62NmgowIAWYwUxSgQTYaxLMHiCM1uDnW/0Pu7aUtg3wV/IAAAAASUVORK5CYII="

export const heroBase64Light = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAJCAIAAABi7eEAAAAACXBIWXMAAAsTAAALEwEAmpwYAAABNElEQVQYlS3BPXITQRAG0K97e2b2RxK2V2UKCnIS4Apcm4jiCgSOCEgoF4FXWLK0q+3pbhLeo98/H7ZbllzCIqhhMDUGsxAh84iICEritU7TkcexLaV0ww2xzZcp4rQsM1IOVcBLJ/32FSPqqu/evubD4XC9XkHJzMx00WoUYXY8n4lYVQOm6rPO06lKKkPfd4BLU3Ia+74vuxu4dW0HaUENTDcDNrvt5fgsJTURADznJqd7bjuoola099AKB5jROIIAllrXp6fTfi9cbr9/+1rtsq54/CPjXS3ip5f66eP7D5+/YH0GQkQkpeRAU8+OPZH2Q74bc9tqEi9OTeqhCxxBoFj/ghnudTlIfoNSEAEQCP/ZCp3CjZpMj79+7DYDAEdEBDO7u5mJCBFFBBBEHOYvl/kfGRuxaCfnassAAAAASUVORK5CYII="

export const aboutBase64 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAICAIAAACpsTKlAAAACXBIWXMAAAsTAAALEwEAmpwYAAABRElEQVQYlQXBzWpTQRgG4He+mczJOScxgRpbXCgiQgu6ansFXQguvI/enBfgpjvBpYIICqXY0tQmHJLzN//z9Xnk1eW3V4vTunxd69VUL5QsSWhJhSLNmQAFCEAKSAFJ2RjvjQ+jJvgUOm988BCo6jqBGXEihBSCwQyoH/f/fj5e/23WE5ZHkxcRXa+pKqfRm4PZM44huX7PAQAANSNzfrD6+O5QCSXSoKqTG28d29vdpvX2gxRhbLfBZy9+bx/UxfuXnS323CS4vbNvD5fHg4lZuzTfGHesi4rj19vrpjFnzxfqy6+rP5ub/7vHk3l9P7TboWvdEJMkmXKMR9XSsf2+3q7KYjVX4vObT3f9w13fLCa0Hrs+jqUoIntCIAHDERBLWjLSmK30mO18G9mtxy5znEpFImtJJiMgEQggz9ayLUg/AR2wt0pbwkYKAAAAAElFTkSuQmCC"

export const firstFeatureCardBase64 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAIAAAACUFjqAAAACXBIWXMAAAsTAAALEwEAmpwYAAABQUlEQVQYlQE2Acn+AVkmKDIhD5byD/Hu8QsdIBAKDPfw8AwXFyTt+7zKvwQoAA/w3gkEEf0BAwkh+vP+6OPxFxgOHzDrDfkH7+UEKB8jDAAA6vr6thUh8gsFQgXXBfPq1v/2Bu/eHRUUBL4SD0slHnMcGT706c/+DdvV+u3X/CkK/fYJ9OHY1gMqEAq67v/+JC1fJvs66MCr0uTjCBAWAP4B7Ofj3dcEJCEJ6On3u+H19gQlDLOv8cvEJUtD+AIwDPz4GBcZBPcOFTYkNp7g7Onu8RksQAcODPj6/ufLrfDs/RwVDwPWCzAo/t1BOhvf5/fQ2uztBQ7k4+Lq6egCAO/07eoD+AIC39TVDOHcACUg/xEBBdzuC/P6EQPsA/X3Dv3+AwwAAvcKEdbr9fjs5gP+8hbp4hP6APT07On4+BwIAi4WnAigYalwAAAAAElFTkSuQmCC"

export const secondFeatureCardBase64 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAIAAAACUFjqAAAACXBIWXMAAAsTAAALEwEAmpwYAAABEElEQVQYlQXBaU7CQBgA0Jn5ZmuntEhiLQmK0QR/GPUCXt0jyAEkakhYTKUtONTgrL6HZTHK08wB94yrdJAycvRRrz4IFygEGhCyXJ3+eulN9fTcuuDmL1zlwBOCETCpBKUOaHE+KUYXfDz1aohDSDhX1RXxhErbU92Y9Kwzzu3r7PpO/B4YTwZKkRGgtQNLRfM2P0Y6mVzn7pgyNhuAaDbEIJQTRDAaJ+zBfBnn9fKTp4rf3ncgwIhCIgsYDcspk+KbZ1EW2+V7fdCuqeGxHBImdjaeMBxwgrttWZVSt6HZdvsdYCaNVNaH2HdJko0vZ369WC0Xrf6hOEIPLAuWcakdvhG2Evh1s9NtrTjEGP8BFiZ8RK1pxAUAAAAASUVORK5CYII="

// export const aboutHeroImageBase64 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAICAIAAACpsTKlAAAACXBIWXMAAAsTAAALEwEAmpwYAAABRElEQVQYlQXBzWpTQRgG4He+mczJOScxgRpbXCgiQgu6ansFXQguvI/enBfgpjvBpYIICqXY0tQmHJLzN//z9Xnk1eW3V4vTunxd69VUL5QsSWhJhSLNmQAFCEAKSAFJ2RjvjQ+jJvgUOm988BCo6jqBGXEihBSCwQyoH/f/fj5e/23WE5ZHkxcRXa+pKqfRm4PZM44huX7PAQAANSNzfrD6+O5QCSXSoKqTG28d29vdpvX2gxRhbLfBZy9+bx/UxfuXnS323CS4vbNvD5fHg4lZuzTfGHesi4rj19vrpjFnzxfqy6+rP5ub/7vHk3l9P7TboWvdEJMkmXKMR9XSsf2+3q7KYjVX4vObT3f9w13fLCa0Hrs+jqUoIntCIAHDERBLWjLSmK30mO18G9mtxy5znEpFImtJJiMgEQggz9ayLUg/AR2wt0pbwkYKAAAAAElFTkSuQmCC"